import { Component, ElementRef, EventEmitter, forwardRef, Input, OnInit, OnDestroy, Output, ViewChild, inject, ChangeDetectorRef, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { HisControlValueAccessor } from '../../core/forms/his-control-value-accessor';
import { HisScriptLoaderService } from '../../core/service/base/his-script-loader.service';
import { HisBaseGlobalsService } from '../../core/service/base/his-base-globals.service';

declare const window: Window & typeof globalThis & {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    CM6Editor: any;
};

@Component( {
    selector: 'his-code-editor',
    imports: [],
    templateUrl: './his-code-editor.component.html',
    styleUrl: './his-code-editor.component.css',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef( () => HisCodeEditorComponent ),
            multi: true,
        },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: true
} )
export class HisCodeEditorComponent implements HisControlValueAccessor, OnInit, OnDestroy, AfterViewInit {
    onModelChange: any;
    onModelTouched: any;
    private cdr = inject( ChangeDetectorRef );
    private scriptLoader = inject( HisScriptLoaderService );
    private hisBaseGlobalsService = inject( HisBaseGlobalsService );

    @ViewChild( 'editor', { static: true } ) editorRef!: ElementRef<HTMLElement>;

    private view: any = null;
    private editorInitialized = false;
    private useNgModel = false;

    @Input() mode: string = 'javascript';
    @Input() lineNumbers: boolean = true;
    @Input() lineWrapping: boolean = true;
    @Input() readOnly: boolean = false;
    @Input() height: string = '400px';
    @Input() placeholder: string = 'Code hier eingeben...';
    @Output() dataChange: EventEmitter<string> = new EventEmitter<string>();

    private _data: string = '';
    public scriptsLoaded = false;

    @Input()
    set data( value: string ) {
        if ( value === undefined ) return;
        if ( this.useNgModel ) {
            console.warn( '[his-code-editor] ngModel verwendet - set data ist nicht erlaubt.', value );
            return;
        }
        this._data = value || '';
        this.updateEditorContent();
    }
    get data(): string {
        return this._data;
    }

    config: any;

    ngOnInit(): void {
        this.config = this.getConfig();

        this.loadCodeMirror()
            .then( () => {
                this.scriptsLoaded = true;
                this.initCodeMirror();
            } )
            .catch( err => {
                console.error( '[his-code-editor] Fehler beim Laden der CodeMirror-Abhängigkeiten', err );
            } );
    }

    ngAfterViewInit(): void {
        if ( this.scriptsLoaded ) {
            this.initCodeMirror();
        }
    }

    async loadCodeMirror(): Promise<void> {
        const jsfScripts = [
            { 'name': 'core-bundle.js', 'library': 'js/extras/codemirror6' },
            { 'name': 'multi-language-bundle.js', 'library': 'js/extras/codemirror6' }
        ];
        await this.scriptLoader.loadJSFScriptsInOrder( jsfScripts );
        await this.scriptLoader.waitFor( () => window.CM6Editor, 5000 );
    }

    private initCodeMirror(): void {
        if ( this.editorInitialized || !this.editorRef?.nativeElement || !window.CM6Editor ) return;

        this.view = window.CM6Editor.createEditor( {
            parent: this.editorRef.nativeElement,
            doc: this._data,
            language: this.mode,
            readOnly: this.readOnly,
            height: this.height,
            extensions: [
                window.CM6Editor.EditorView.updateListener.of( ( update: any ) => {
                    if ( !update.docChanged ) return;
                    const newValue = update.state.doc.toString();
                    if ( newValue !== this._data ) {
                        this._data = newValue;
                        this.dataChange.emit( this._data );
                        this.onChange( this._data );
                        this.onTouched();
                        this.cdr.markForCheck();
                    }
                } )
            ]
        } );

        this.editorInitialized = true;
    }

    private updateEditorContent(): void {
        if ( !this.view || !this.editorInitialized ) return;
        if ( this.view.getValue() !== this._data ) {
            this.view.setValue( this._data );
        }
    }

    ngOnDestroy(): void {
        if ( this.view ) {
            this.view.destroy();
            this.view = null;
        }
    }

    getConfig(): any {
        return {
            mode: this.mode,
            lineNumbers: this.lineNumbers,
            lineWrapping: this.lineWrapping,
            readOnly: this.readOnly,
            height: this.height,
            placeholder: this.placeholder,
        };
    }

    private onChange: ( value: string ) => void = () => { };
    private onTouched: () => void = () => { };

    writeValue( value: string ): void {
        this.useNgModel = true;
        this._data = value || '';
        this.updateEditorContent();
    }

    registerOnChange( fn: ( value: string ) => void ): void {
        this.onChange = fn;
    }

    registerOnTouched( fn: () => void ): void {
        this.onTouched = fn;
    }

    setDisabledState( isDisabled: boolean ): void {
        this.readOnly = isDisabled;
        if ( this.view ) {
            this.view.setReadOnly( isDisabled );
        }
    }
}